const PHONE_RULES = {
  '+44': { min: 10, max: 10, mobilePrefixes: ['7'] },
  '+353': { min: 9, max: 9, mobilePrefixes: ['8'] },
  '+33': { min: 9, max: 9, mobilePrefixes: ['6', '7'] },
  '+49': { min: 10, max: 11, mobilePrefixes: ['15', '16', '17'] },
  '+31': { min: 9, max: 9, mobilePrefixes: ['6'] },
  '+39': { min: 9, max: 10, mobilePrefixes: ['3'] },
  '+34': { min: 9, max: 9, mobilePrefixes: ['6', '7'] },
  '+351': { min: 9, max: 9, mobilePrefixes: ['9'] },
  '+32': { min: 9, max: 9, mobilePrefixes: ['4'] },
  '+41': { min: 9, max: 9, mobilePrefixes: ['7'] },
  '+43': { min: 10, max: 13, mobilePrefixes: ['6'] },
}

const DEFAULT_PHONE_RULE = { min: 6, max: 14 }

class KlaviyoSignup extends HTMLElement {
  constructor () {
    super()
    this.currentViewIndex = 0
    this.isTransitioning = false
    this.formData = {}
    this.views = []
  }

  connectedCallback () {
    this.endpointUrl = this.dataset.endpointUrl
    this.store = this.dataset.store
    this.defaultListId = this.dataset.listId || ''
    this.signupSource = this.dataset.signupSource || ''
    this.namePlaceholder = this.dataset.namePlaceholder || 'First name'
    this.emailPlaceholder = this.dataset.emailPlaceholder || 'Email'
    this.phonePlaceholder = this.dataset.phonePlaceholder || 'Phone number'
    this.countryCodeLabel = this.dataset.countryCodeLabel || 'Country'
    this.continueButtonText = this.dataset.continueButtonText || 'Continue'
    this.submitButtonText = this.dataset.submitButtonText || 'Sign up'
    this.successMessage = this.dataset.successMessage || 'Thank you for signing up!'
    this.errorMessage = this.dataset.errorMessage || 'Something went wrong. Please try again.'
    this.loadingMessage = this.dataset.loadingMessage || 'Processing...'
    this.phoneExample = this.dataset.phoneExample || '7123456789'
    this.validationFirstName = this.dataset.validationFirstName || 'Please enter your first name'
    this.validationEmail = this.dataset.validationEmail || 'Please enter a valid email address'
    this.validationCountry = this.dataset.validationCountry || 'Please select a country code'
    this.validationPhone = this.dataset.validationPhone || 'Please enter a valid phone number'
    this.validationPhoneSms = this.dataset.validationPhoneSms || 'Please enter a valid mobile number that can receive SMS'
    this.successIcon = this.dataset.successIcon || ''
    this.errorIcon = this.dataset.errorIcon || ''
    this.emailSubmitted = false
    this.localeCountry = (this.dataset.localeCountry || '').toUpperCase()
    this.trackingConsentCountries = (this.dataset.trackingConsentCountries || '')
      .split(',')
      .map(code => code.trim().toUpperCase())
      .filter(Boolean)
    this.trackingConsentActive = false

    this.parseViews()
    this.renderSmsForms()
    this.setupEventListeners()
    this.setupTrackingConsent()
    this.syncContainerMinHeight()
    this.bindResizeHandler()
  }

  bindResizeHandler () {
    if (this._resizeHandler) return
    let resizeTimer
    this._resizeHandler = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => this.syncContainerMinHeight(), 150)
    }
    window.addEventListener('resize', this._resizeHandler)
  }

  disconnectedCallback () {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
      this._resizeHandler = null
    }
  }

  isDesktopLayout () {
    return typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(min-width: 1024px)').matches
  }

  measureElementHeight (element) {
    if (!element) return 0

    const isResults = element.classList.contains('klaviyo-signup__view--results')
    const previous = {
      display: element.style.display,
      flexDirection: element.style.flexDirection,
      justifyContent: element.style.justifyContent,
      visibility: element.style.visibility,
      position: element.style.position,
      left: element.style.left,
      right: element.style.right,
      pointerEvents: element.style.pointerEvents,
      width: element.style.width,
    }

    if (isResults) {
      element.style.display = 'flex'
      element.style.flexDirection = 'column'
      element.style.justifyContent = 'center'
    } else {
      element.style.display = 'block'
    }

    element.style.visibility = 'hidden'
    element.style.position = 'absolute'
    element.style.left = '0'
    element.style.right = '0'
    element.style.width = '100%'
    element.style.pointerEvents = 'none'

    const height = element.scrollHeight

    element.style.display = previous.display
    element.style.flexDirection = previous.flexDirection
    element.style.justifyContent = previous.justifyContent
    element.style.visibility = previous.visibility
    element.style.position = previous.position
    element.style.left = previous.left
    element.style.right = previous.right
    element.style.pointerEvents = previous.pointerEvents
    element.style.width = previous.width

    return height
  }

  measureFormViewsMaxHeight () {
    let maxHeight = 0

    this.views.forEach((view) => {
      maxHeight = Math.max(maxHeight, this.measureElementHeight(view.element))
    })

    return maxHeight
  }

  measureResultsViewHeight () {
    if (!this.resultsView) return 0

    let maxHeight = this.measureElementHeight(this.resultsView)
    const resultsContainer = this.resultsView.querySelector('.klaviyo-signup__results')

    if (resultsContainer) {
      const savedHtml = resultsContainer.innerHTML
      ;['loading', 'success', 'error'].forEach((state) => {
        this.renderResults(state)
        maxHeight = Math.max(maxHeight, this.measureElementHeight(this.resultsView))
      })
      resultsContainer.innerHTML = savedHtml
    }

    return maxHeight
  }

  getActiveView () {
    return this.querySelector('.klaviyo-signup__view--active')
  }

  setContainerDimensions (height, { lock = false } = {}) {
    const container = this.querySelector('.klaviyo-signup__views-container')
    if (!container) return

    if (height > 0) {
      container.style.minHeight = `${height}px`
      container.style.height = lock ? `${height}px` : ''
      return
    }

    container.style.minHeight = ''
    container.style.height = ''
  }

  syncContainerMinHeight () {
    const activeView = this.getActiveView()

    if (!activeView) {
      this.setContainerDimensions(0)
      return
    }

    if (activeView.classList.contains('klaviyo-signup__view--results')) {
      const height = this.isDesktopLayout()
        ? this.measureFormViewsMaxHeight()
        : Math.max(this.measureFormViewsMaxHeight(), this.measureResultsViewHeight())
      this.setContainerDimensions(height, { lock: true })
      return
    }

    this.setContainerDimensions(this.measureElementHeight(activeView))
  }

  setTransitionMinHeight (fromView, toView) {
    const toIsResults = toView?.classList.contains('klaviyo-signup__view--results')

    if (toIsResults) {
      const height = Math.max(
        this.measureElementHeight(fromView),
        this.measureFormViewsMaxHeight(),
        this.measureResultsViewHeight(),
      )
      this.setContainerDimensions(height, { lock: true })
      return
    }

    const height = Math.max(
      this.measureElementHeight(fromView),
      this.measureElementHeight(toView),
    )

    this.setContainerDimensions(height)
  }

  parseViews () {
    const viewElements = this.querySelectorAll('.klaviyo-signup__view:not(.klaviyo-signup__view--results)')
    this.views = Array.from(viewElements).map((el, index) => ({
      element: el,
      type: el.dataset.viewType,
      index,
      captureFirstName: el.dataset.captureFirstName === 'true',
      listId: el.dataset.listId?.trim() || '',
    }))
    this.resultsView = this.querySelector('.klaviyo-signup__view--results')
  }

  renderSmsForms () {
    this.views.forEach((view, index) => {
      if (view.type === 'sms') {
        const formFields = view.element.querySelector('.klaviyo-signup__form-fields')
        const isLastView = index === this.views.length - 1
        formFields.innerHTML = this.renderSmsForm(isLastView, index)
      }
    })
  }

  renderSmsForm (isLastView, viewIndex) {
    const defaultCountryCode = this.getDefaultCountryCode()
    const countryOptions = this.getCountryCodeOptions()
    const selectedCountry = countryOptions.find(c => c.code === defaultCountryCode) || countryOptions[0]
    const popoverId = `country-code-popover-${viewIndex}`

    return `
      <div class="flex flex-col items-stretch w-full md:flex-row md:items-end gap-3">
        <div class="klaviyo-signup__field w-full md:flex-1 min-w-0">
          <label for="phone-${viewIndex}" class="text-sm text-iron mb-1 block">
            <span>${this.phonePlaceholder}</span>
          </label>
          <div class="flex gap-0 group klaviyo-signup__phone-group w-full min-w-0">
            <div class="klaviyo-signup__country-selector relative shrink-0">
              <input type="hidden" name="country_code" value="${selectedCountry.code}" required />
              <button
                type="button"
                popovertarget="${popoverId}"
                class="klaviyo-signup__country-button h-11.5 rounded-r-none border border-steel px-2.5 md:px-3 focus-visible:outline-hidden text-base text-black focus:ring-0 focus:border-black group-hover:border-black group-focus:border-black group-active:border-black flex items-center gap-1.5 bg-white"
                aria-label="${this.countryCodeLabel}"
              >
                <span class="klaviyo-signup__country-flag w-5 h-5 flex items-center justify-center">${selectedCountry.flag}</span>
                <span class="klaviyo-signup__country-code shrink-0">${selectedCountry.code}</span>
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>
              <div
                id="${popoverId}"
                popover
                class="klaviyo-signup__country-popover bg-white border border-steel rounded-lg shadow-lg p-0 m-0 max-h-75 overflow-y-auto"
              >
                ${countryOptions.map(country => `
                  <button
                    type="button"
                    class="klaviyo-signup__country-option w-full flex items-center gap-2 px-3 py-2 hover:bg-oat transition-colors text-left"
                    data-country-code="${country.code}"
                  >
                    <span class="w-5 h-5 flex items-center justify-center flex-shrink-0">${country.flag}</span>
                    <span class="text-base">${country.code} ${country.name}</span>
                  </button>
                `).join('')}
              </div>
            </div>
            <input
              type="tel"
              name="phone"
              id="phone-${viewIndex}"
              placeholder="${this.phoneExample}"
              aria-label="${this.phonePlaceholder}"
              class="klaviyo-signup__input h-11.5 flex-1 min-w-0 w-full rounded-l-none border border-l-0 border-steel px-3.5 py-2.5 md:px-4.5 md:py-3 placeholder-battleship focus-visible:outline-hidden text-base text-black focus:ring-0 focus:border-black group-hover:border-black group-focus:border-black group-active:border-black"
              required
            />
          </div>
          <div class="klaviyo-signup__error text-danger text-sm mt-1 hidden"><span>${this.validationPhone}</span></div>
        </div>
        <button
          type="button"
          class="klaviyo-signup__button button button-primary h-11.5 w-full md:w-fit md:flex-shrink-0 mt-2.5"
          data-action="${isLastView ? 'submit' : 'continue'}"
        >
          <span>${isLastView ? this.submitButtonText : this.continueButtonText}</span>
        </button>
      </div>
    `
  }

  getCountryCodeOptions () {
    const flags = window.Tentbox?.storeSelector?.flags || {}

    return [
      { code: '+44', name: 'UK', flag: this.getCountryFlag('gb', flags) },
      { code: '+353', name: 'Ireland', flag: this.getCountryFlag('ie', flags) },
      { code: '+33', name: 'France', flag: this.getCountryFlag('fr', flags) },
      { code: '+49', name: 'Germany', flag: this.getCountryFlag('de', flags) },
      { code: '+31', name: 'Netherlands', flag: this.getCountryFlag('nl', flags) },
      { code: '+39', name: 'Italy', flag: this.getCountryFlag('it', flags) },
      { code: '+34', name: 'Spain', flag: this.getCountryFlag('es', flags) },
      { code: '+351', name: 'Portugal', flag: this.getCountryFlag('pt', flags) },
      { code: '+32', name: 'Belgium', flag: this.getCountryFlag('be', flags) },
      { code: '+41', name: 'Switzerland', flag: this.getCountryFlag('ch', flags) },
      { code: '+43', name: 'Austria', flag: this.getCountryFlag('at', flags) },
    ]
  }

  getCountryFlag (countryCode, flags) {
    const flagKey = `${countryCode}_circle`
    if (flags[flagKey]) {
      return flags[flagKey]
    }

    const flagEmojis = {
      nl: '🇳🇱',
      es: '🇪🇸',
      pt: '🇵🇹',
      be: '🇧🇪',
      ch: '🇨🇭',
      at: '🇦🇹',
    }

    if (flagEmojis[countryCode]) {
      return `<span class="text-lg">${flagEmojis[countryCode]}</span>`
    }

    return '<span class="text-lg">🏴</span>'
  }

  getDefaultCountryCode () {
    const shopPermanentDomain = window.Shopify?.shop || ''

    if (shopPermanentDomain === 'tentbox-6623.myshopify.com') {
      return '+44'
    }

    try {
      const geoData = localStorage.getItem('geolocationData')
      if (geoData) {
        const parsed = JSON.parse(geoData)
        const countryCode = parsed.country_code

        const countryToPhone = {
          GB: '+44',
          IE: '+353',
          FR: '+33',
          DE: '+49',
          NL: '+31',
          IT: '+39',
          ES: '+34',
          PT: '+351',
          BE: '+32',
          CH: '+41',
          AT: '+43',
        }

        return countryToPhone[countryCode] || '+44'
      }
    } catch (error) {
      console.log('Could not parse geolocation data:', error)
    }

    return '+44'
  }

  setupTrackingConsent () {
    this.trackingConsentBlocks = Array.from(this.querySelectorAll('[data-tracking-consent]'))

    if (!this.trackingConsentBlocks.length || !this.trackingConsentCountries.length) return

    this.applyTrackingConsentVisibility()

    if (!this.trackingConsentActive) {
      this.pollGeoCountry()
    }
  }

  getGeoCountry () {
    try {
      const session = sessionStorage.getItem('tentboxSessionIpInfo')
      if (session) {
        const parsed = JSON.parse(session)
        const countryCode = parsed?.country_code || parsed?.country
        if (countryCode) return String(countryCode).toUpperCase()
      }
    } catch (error) {
      // ignore malformed cache
    }

    try {
      const cached = localStorage.getItem('tentboxIpInfo')
      if (cached) {
        const parsed = JSON.parse(cached)
        const countryCode = parsed?.data?.country_code || parsed?.data?.country
        if (countryCode) return String(countryCode).toUpperCase()
      }
    } catch (error) {
      // ignore malformed cache
    }

    return ''
  }

  applyTrackingConsentVisibility () {
    const detectedCountry = this.getGeoCountry()

    this.trackingConsentActive = this.trackingConsentCountries.includes(this.localeCountry) ||
      this.trackingConsentCountries.includes(detectedCountry)

    this.trackingConsentBlocks.forEach(block => {
      block.classList.toggle('hidden', !this.trackingConsentActive)
    })
  }

  pollGeoCountry (attempt = 0) {
    const maxAttempts = 15

    if (attempt >= maxAttempts) return

    setTimeout(() => {
      this.applyTrackingConsentVisibility()

      if (!this.trackingConsentActive) {
        this.pollGeoCountry(attempt + 1)
      }
    }, 200)
  }

  setupEventListeners () {
    this.querySelectorAll('.klaviyo-signup__button').forEach(button => {
      button.addEventListener('click', (e) => this.handleButtonClick(e))
    })

    this.querySelectorAll('.klaviyo-signup__input').forEach(input => {
      input.addEventListener('input', () => this.clearError(input))
    })

    this.setupCountrySelector()
  }

  setupCountrySelector () {
    this.querySelectorAll('.klaviyo-signup__country-selector').forEach(selector => {
      const button = selector.querySelector('.klaviyo-signup__country-button')
      const popover = selector.querySelector('[popover]')
      const countryOptions = selector.querySelectorAll('.klaviyo-signup__country-option')

      if (button && popover) {
        const supportsAnchorPositioning = CSS.supports('anchor-name', '--test')

        if (!supportsAnchorPositioning) {
          const positionPopover = () => {
            const buttonRect = button.getBoundingClientRect()
            popover.style.left = `${buttonRect.left}px`
            popover.style.top = `${buttonRect.bottom + 4}px`
          }

          const handleScroll = () => {
            if (popover.matches(':popover-open')) {
              positionPopover()
            }
          }

          button.addEventListener('click', () => {
            requestAnimationFrame(positionPopover)
          })

          popover.addEventListener('toggle', (e) => {
            if (e.newState === 'open') {
              window.addEventListener('scroll', handleScroll, true)
              window.addEventListener('resize', handleScroll)
            } else {
              window.removeEventListener('scroll', handleScroll, true)
              window.removeEventListener('resize', handleScroll)
            }
          })
        }
      }

      countryOptions.forEach(option => {
        option.addEventListener('click', (e) => {
          const countryCode = e.currentTarget.dataset.countryCode
          this.selectCountryCode(countryCode, selector)

          if (popover) {
            popover.hidePopover()
          }
        })
      })
    })
  }

  selectCountryCode (code, selector) {
    const scope = selector || this
    const hiddenInput = scope.querySelector('input[name="country_code"]')
    const button = scope.querySelector('.klaviyo-signup__country-button')
    const codeSpan = button?.querySelector('.klaviyo-signup__country-code')
    const flagSpan = button?.querySelector('.klaviyo-signup__country-flag')

    if (hiddenInput) {
      hiddenInput.value = code
    }

    if (codeSpan) {
      codeSpan.textContent = code
    }

    if (flagSpan) {
      const countryOptions = this.getCountryCodeOptions()
      const selectedCountry = countryOptions.find(c => c.code === code)
      if (selectedCountry) {
        flagSpan.innerHTML = selectedCountry.flag
      }
    }
  }

  async handleButtonClick (e) {
    const button = e.currentTarget
    const action = button.dataset.action

    if (this.isTransitioning) return

    const currentView = this.views[this.currentViewIndex]
    const isValid = this.validateCurrentView(currentView)

    if (!isValid) return

    this.collectCurrentViewData(currentView)

    if (action === 'submit') {
      const step = currentView.type === 'sms' ? 'sms' : 'email'
      await this.submitForm(step)
      return
    }

    if (currentView.type === 'email') {
      const submitted = await this.submitEmailStep(currentView)
      if (!submitted) return
    }

    this.goToNextView()
  }

  getActiveListId () {
    const viewListId = this.views[this.currentViewIndex]?.listId
    return viewListId || this.defaultListId
  }

  getSignupPage () {
    if (typeof window === 'undefined' || !window.location?.pathname) {
      return ''
    }

    return window.location.pathname
  }

  buildPayload (step) {
    /* eslint-disable camelcase */
    const payload = {
      email: this.formData.email,
      list_id: this.getActiveListId(),
      store: this.store,
      step,
    }

    if (this.formData.first_name) {
      payload.first_name = this.formData.first_name
    }

    if (step === 'sms' && this.formData.phone) {
      payload.phone = this.formData.phone
    }

    if (this.formData.open_tracking_consent) {
      payload.open_tracking_consent = this.formData.open_tracking_consent
    }

    if (this.signupSource) {
      payload.api_signup_source = this.signupSource
    }

    const signupPage = this.getSignupPage()
    if (signupPage) {
      payload.api_signup_page = signupPage
    }
    /* eslint-enable camelcase */

    return payload
  }

  async postSignup (step) {
    return fetch(this.endpointUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(this.buildPayload(step)),
    })
  }

  async submitEmailStep (view) {
    const formContainer = view.element.querySelector('.klaviyo-signup__form')
    const emailInput = formContainer.querySelector('input[name="email"]')
    const button = view.element.querySelector('[data-action="continue"]')

    this.setButtonLoading(button, true)

    try {
      const response = await this.postSignup('email')
      const result = await this.parseSignupResponse(response)

      if (!result.ok) {
        this.showError(emailInput, result.error || this.errorMessage)
        return false
      }

      this.emailSubmitted = true
      this.trackSubmit('email')
      return true
    } catch (error) {
      console.error('Klaviyo email signup error:', error)
      this.showError(emailInput, this.errorMessage)
      return false
    } finally {
      this.setButtonLoading(button, false)
    }
  }

  trackSubmit (step) {
    try {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'klaviyo_submit',
        // eslint-disable-next-line camelcase
        signup_type: step === 'sms' ? 'sms' : 'email',
        // eslint-disable-next-line camelcase
        signup_location: this.signupSource || 'Website signup',
      })
    } catch (error) {
      console.warn('Error tracking signup:', error)
    }
  }

  setButtonLoading (button, loading) {
    if (!button) return
    button.classList.toggle('klaviyo-signup__button--loading', loading)
    button.disabled = loading
    button.setAttribute('aria-busy', loading ? 'true' : 'false')
  }

  async parseSignupResponse (response) {
    let data = {}
    try {
      data = await response.json()
    } catch {
      data = {}
    }

    if (response.ok) {
      return { ok: true, queued: data.queued === true }
    }

    return { ok: false, field: data.field, error: data.error }
  }

  validateCurrentView (view) {
    const formContainer = view.element.querySelector('.klaviyo-signup__form')
    let isValid = true

    if (view.type === 'email') {
      const emailInput = formContainer.querySelector('input[name="email"]')
      const firstNameInput = formContainer.querySelector('input[name="first_name"]')

      if (firstNameInput && !firstNameInput.value.trim()) {
        this.showError(firstNameInput, this.validationFirstName)
        isValid = false
      }

      if (!emailInput.value.trim() || !this.isValidEmail(emailInput.value)) {
        this.showError(emailInput, this.validationEmail)
        isValid = false
      }
    } else if (view.type === 'sms') {
      const countryCodeInput = formContainer.querySelector('input[name="country_code"]')
      const phoneInput = formContainer.querySelector('input[name="phone"]')

      if (!countryCodeInput || !countryCodeInput.value) {
        this.showError(phoneInput, this.validationCountry)
        isValid = false
      } else if (!phoneInput.value.trim()) {
        this.showError(phoneInput, this.validationPhone)
        isValid = false
      } else {
        const result = this.validatePhoneNumber(countryCodeInput.value, phoneInput.value)
        if (!result.valid) {
          this.showError(phoneInput, this.phoneErrorMessage(result.reason))
          isValid = false
        }
      }
    }

    return isValid
  }

  collectCurrentViewData (view) {
    const formContainer = view.element.querySelector('.klaviyo-signup__form')

    if (view.type === 'email') {
      const emailInput = formContainer.querySelector('input[name="email"]')
      const firstNameInput = formContainer.querySelector('input[name="first_name"]')
      const trackingConsentInput = formContainer.querySelector('input[name="open_tracking_consent"]')

      this.formData.email = emailInput.value.trim()
      if (firstNameInput) {
        // eslint-disable-next-line camelcase
        this.formData.first_name = firstNameInput.value.trim()
      }

      // Re-check right before submit, since GeoIP may have resolved since page load
      this.applyTrackingConsentVisibility()
      if (trackingConsentInput && this.trackingConsentActive) {
        // eslint-disable-next-line camelcase
        this.formData.open_tracking_consent = trackingConsentInput.checked ? 'SUBSCRIBED' : 'UNSUBSCRIBED'
      }
    } else if (view.type === 'sms') {
      const countryCodeInput = formContainer.querySelector('input[name="country_code"]')
      const phoneInput = formContainer.querySelector('input[name="phone"]')

      this.formData.phone = this.formatPhoneNumber(countryCodeInput.value, phoneInput.value)
    }
  }

  normaliseNationalNumber (countryCode, phone) {
    let digits = String(phone).replace(/\D/g, '')
    const cc = String(countryCode).replace(/\D/g, '')
    const rule = PHONE_RULES[countryCode] || DEFAULT_PHONE_RULE

    if (digits.startsWith('00')) {
      digits = digits.slice(2)
    }

    if (cc && digits.startsWith(cc) && digits.length > rule.max) {
      const stripped = digits.slice(cc.length)
      if (stripped.length >= rule.min && stripped.length <= rule.max) {
        digits = stripped
      }
    }

    if (digits.startsWith('0')) {
      digits = digits.slice(1)
    }

    return digits
  }

  formatPhoneNumber (countryCode, phone) {
    return `${countryCode}${this.normaliseNationalNumber(countryCode, phone)}`
  }

  validatePhoneNumber (countryCode, phone) {
    const national = this.normaliseNationalNumber(countryCode, phone)
    const rule = PHONE_RULES[countryCode] || DEFAULT_PHONE_RULE

    if (national.length < rule.min || national.length > rule.max) {
      return { valid: false, reason: 'length' }
    }

    if (rule.mobilePrefixes && !rule.mobilePrefixes.some(prefix => national.startsWith(prefix))) {
      return { valid: false, reason: 'mobile' }
    }

    const e164 = `${countryCode}${national}`
    if (!this.isValidE164(e164)) {
      return { valid: false, reason: 'format' }
    }

    return { valid: true, e164 }
  }

  phoneErrorMessage (reason) {
    if (reason === 'mobile') {
      return this.validationPhoneSms
    }
    return this.validationPhone
  }

  isValidEmail (email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  isValidE164 (phone) {
    return /^\+[1-9]\d{6,14}$/.test(phone)
  }

  showError (input, message) {
    input.classList.add('border-danger')
    const errorDiv = input.closest('.klaviyo-signup__field').querySelector('.klaviyo-signup__error')
    if (errorDiv) {
      errorDiv.querySelector('span').textContent = message
      errorDiv.classList.remove('hidden')
    }
  }

  clearError (input) {
    input.classList.remove('border-danger')
    const errorDiv = input.closest('.klaviyo-signup__field').querySelector('.klaviyo-signup__error')
    if (errorDiv) {
      errorDiv.classList.add('hidden')
    }
  }

  transitionToView (nextViewElement, { onComplete } = {}) {
    return new Promise((resolve) => {
      const currentView = this.querySelector('.klaviyo-signup__view--active')
      this.isTransitioning = true
      this.setTransitionMinHeight(currentView, nextViewElement)

      if (currentView) {
        currentView.style.willChange = 'opacity'
        currentView.classList.add('klaviyo-signup__view--leaving')
      }

      setTimeout(() => {
        if (currentView) {
          currentView.classList.remove('klaviyo-signup__view--active', 'klaviyo-signup__view--leaving')
          currentView.style.willChange = ''
        }

        nextViewElement.classList.add('klaviyo-signup__view--active', 'klaviyo-signup__view--entering')
        nextViewElement.style.willChange = 'opacity, transform'

        requestAnimationFrame(() => {
          nextViewElement.classList.add('klaviyo-signup__view--entering-active')
        })

        setTimeout(() => {
          nextViewElement.classList.remove('klaviyo-signup__view--entering', 'klaviyo-signup__view--entering-active')
          nextViewElement.style.willChange = ''
          this.isTransitioning = false
          this.syncContainerMinHeight()

          if (typeof onComplete === 'function') {
            onComplete()
          }

          const firstInput = nextViewElement.querySelector('input:not([type="hidden"]), select, button')
          if (firstInput && firstInput.type !== 'button') {
            firstInput.focus()
          }

          resolve()
        }, 300)
      }, 200)
    })
  }

  goToNextView () {
    if (this.currentViewIndex < this.views.length - 1) {
      const nextView = this.views[this.currentViewIndex + 1].element

      this.transitionToView(nextView, {
        onComplete: () => {
          this.currentViewIndex++
        },
      })
    }
  }

  async submitForm (step) {
    const formViewElement = this.views[this.currentViewIndex].element
    this.renderResults('loading')
    const transitionDone = this.transitionToView(this.resultsView)

    let result
    try {
      const response = await this.postSignup(step)
      result = await this.parseSignupResponse(response)
    } catch (error) {
      console.error('Klaviyo signup error:', error)
      result = { ok: false }
    }

    await transitionDone

    if (result.ok) {
      this.trackSubmit(step)
      this.swapResults('success')
      return
    }

    if (result.field) {
      await this.transitionToView(formViewElement)
      const input = formViewElement.querySelector(`[name="${result.field}"]`)
      if (input) {
        this.showError(input, result.error || this.phoneErrorMessage('mobile'))
        input.focus()
      }
      return
    }

    this.swapResults('error')
  }

  renderResults (state) {
    const resultsContainer = this.resultsView.querySelector('.klaviyo-signup__results')

    if (state === 'loading') {
      resultsContainer.setAttribute('aria-live', 'polite')
      resultsContainer.innerHTML = `
        <div class="flex flex-col items-center justify-center gap-4">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange" aria-hidden="true"></div>
          <p class="text-center text-lg">${this.loadingMessage}</p>
        </div>
      `
    } else if (state === 'success') {
      resultsContainer.innerHTML = `
        <div class="flex flex-col items-center justify-center gap-4">
          <div class="h-12 w-12 text-success">
            ${this.successIcon}
          </div>
          <p class="text-center text-lg font-semibold">${this.successMessage}</p>
        </div>
      `
    } else if (state === 'error') {
      resultsContainer.innerHTML = `
        <div class="flex flex-col items-center justify-center gap-4">
          <div class="h-12 w-12 text-danger">
            ${this.errorIcon}
          </div>
          <p class="text-center text-lg font-semibold">${this.errorMessage}</p>
        </div>
      `
    }
  }

  swapResults (state) {
    const results = this.resultsView.querySelector('.klaviyo-signup__results')

    results.style.opacity = '0'
    this.renderResults(state)

    requestAnimationFrame(() => {
      results.style.opacity = '1'
    })

    setTimeout(() => {
      results.style.opacity = ''
      this.syncContainerMinHeight()
    }, 300)
  }
}

if (!customElements.get('klaviyo-signup')) {
  customElements.define('klaviyo-signup', KlaviyoSignup)
}
